/**
 * The sport pack contract.
 *
 * A sport is *declarative data* plus optional hooks. `core` and `renderer` know
 * this contract and nothing else — no water polo constant, position name or
 * field dimension may appear outside a pack. The working test for the
 * abstraction: a second sport must be addable as data alone.
 *
 * All geometry below is in normalised field space (see `geometry.ts`).
 */

import type { Vec2 } from './geometry.ts';

export type MarkingStyle = {
  readonly color: string;
  /** Stroke width in field units (fraction of the field's shorter side). */
  readonly width?: number;
  readonly dash?: readonly number[];
  readonly fill?: string;
  readonly opacity?: number;
};

export type Marking =
  | ({ readonly kind: 'line'; readonly id: string; readonly from: Vec2; readonly to: Vec2 } & MarkingStyle)
  | ({
      readonly kind: 'rect';
      readonly id: string;
      readonly at: Vec2;
      readonly size: { readonly w: number; readonly h: number };
    } & MarkingStyle)
  | ({ readonly kind: 'circle'; readonly id: string; readonly at: Vec2; readonly radius: number } & MarkingStyle)
  | ({ readonly kind: 'polyline'; readonly id: string; readonly points: readonly Vec2[] } & MarkingStyle)
  | ({
      readonly kind: 'label';
      readonly id: string;
      readonly at: Vec2;
      readonly text: string;
      /** Text size in field units. */
      readonly size: number;
      readonly align?: 'left' | 'center' | 'right';
      readonly rotate?: number;
    } & MarkingStyle);

/**
 * Space beyond each edge of the field of play, in the surface's own units — the
 * venue the field sits in, not part of the field itself.
 *
 * Given in real units rather than normalised ones because the two axes normalise
 * differently: on a 25 x 20 m field a metre is 1/25 along the length and 1/20
 * across the width. Declaring metres puts that conversion in one place instead
 * of at every call site.
 */
export interface Surround {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export interface Surface {
  /** Real-world size, used for labels and to derive the aspect ratio. */
  readonly dimensions: { readonly length: number; readonly width: number; readonly unit: string };
  /** length / width. The renderer letterboxes the canvas to this. */
  readonly aspectRatio: number;
  readonly background: string;
  /** Drawn in array order, beneath every entity and annotation. */
  readonly markings: readonly Marking[];
  /**
   * How much room beyond the field the surface needs. Declaring it buys reserved
   * space that scales with the board; without it, anything drawn outside [0,1]
   * falls into the renderer's fixed padding and may overflow.
   */
  readonly surround?: Surround;
  /**
   * Venue detail — platforms, benches, officials' equipment — drawn only when the
   * view asks for it. Same normalised field space as `markings`; being outside
   * [0,1] and being optional are separate properties, which is why this is a
   * separate array rather than a flag on `Marking`.
   */
  readonly venueMarkings?: readonly Marking[];
}

export interface TeamDef {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly textColor: string;
  readonly outline: string;
  /** Which goal this team attacks in the default formation. */
  readonly attacking: 'left' | 'right';
}

export interface EntityTypeDef {
  readonly id: string;
  readonly name: string;
  readonly shape: 'disc' | 'ball' | 'cone';
  /** Radius in field units. */
  readonly radius: number;
  readonly draggable: boolean;
  readonly color?: string;
}

export interface RoleDef {
  readonly id: string;
  readonly name: string;
  readonly short: string;
  /** Optional distinct colour, e.g. the goalkeeper cap. */
  readonly color?: string;
  readonly textColor?: string;
}

export interface FormationSlot {
  readonly teamId: string;
  readonly roleId: string;
  readonly label: string;
  readonly at: Vec2;
}

export interface FormationDef {
  readonly id: string;
  readonly name: string;
  readonly slots: readonly FormationSlot[];
  /** Non-player entities placed with this formation, e.g. the ball. */
  readonly props: readonly { readonly typeId: string; readonly at: Vec2; readonly label?: string }[];
}

export interface ToolDefaults {
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly palette: readonly string[];
}

/**
 * A role that does not count toward the team cap, and how many of it a team
 * may hold.
 *
 * The cap exemption is what lets a team field a seventh player, so it is also
 * the one way to get round the cap entirely. `max` is what stops that: without
 * it an exempt role is an unbounded escape hatch.
 */
export interface ExemptRole {
  readonly roleId: string;
  /** How many players of this role a team may hold. */
  readonly max: number;
}

/**
 * How many players a team may field, as data rather than as a rule in `core`.
 *
 * `core` compares these ids as opaque strings and must not learn what any of
 * them mean — `test/architecture.test.ts` fails the build on sport vocabulary in
 * `core`, which is exactly why the exemption lives out here in the pack.
 */
export interface RosterRule {
  /** Maximum players per team that count toward the cap. */
  readonly maxPerTeam: number;
  /** Roles that do not count toward it, each bounded in its own right. */
  readonly exemptRoles: readonly ExemptRole[];
  /** The entity type a roster player is created as. */
  readonly playerTypeId: string;
  /**
   * The standard lineup, in the order `+` fills a team and `−` empties it, so
   * that clearing a team and adding everyone back returns exactly this. Each
   * slot names its team, role, number and spot. Without one, `+` adds a
   * player with no role.
   */
  readonly lineup?: readonly FormationSlot[];
}

/**
 * What travels with a player who has it when the coach draws them swimming.
 *
 * Which entity is carried, and how close it has to be to count, are sport
 * questions, so they arrive as pack data and `core` only ever compares ids as
 * opaque strings and distances as numbers.
 */
export interface PossessionRule {
  /** The entity type that travels with the player. */
  readonly carriedTypeId: string;
  /** How close it must start, in field units, to count as carried. */
  readonly radius: number;
}

export interface SportPack {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly surface: Surface;
  readonly teams: readonly TeamDef[];
  readonly entityTypes: readonly EntityTypeDef[];
  readonly roles: readonly RoleDef[];
  readonly formations: readonly FormationDef[];
  readonly defaultFormationId: string;
  /** Roster limits. A pack without one gets no add/remove controls. */
  readonly roster?: RosterRule;
  /** What a swimmer carries when they have it. A pack without one carries nothing but the player. */
  readonly possession?: PossessionRule;
  readonly tools: ToolDefaults;
  /** i18n-ready terminology. UI strings that name sport concepts come from here. */
  readonly terminology: Readonly<Record<string, string>>;
}

export function findTeam(pack: SportPack, teamId: string | undefined): TeamDef | undefined {
  return pack.teams.find((t) => t.id === teamId);
}

export function findRole(pack: SportPack, roleId: string | undefined): RoleDef | undefined {
  return pack.roles.find((r) => r.id === roleId);
}

export function findEntityType(pack: SportPack, typeId: string): EntityTypeDef | undefined {
  return pack.entityTypes.find((e) => e.id === typeId);
}

export function findFormation(pack: SportPack, formationId: string): FormationDef | undefined {
  return pack.formations.find((f) => f.id === formationId);
}

export function term(pack: SportPack, key: string, fallback?: string): string {
  return pack.terminology[key] ?? fallback ?? key;
}
